import {createAsyncThunk} from '@reduxjs/toolkit';
import {MapNode} from 'common/src/modules/nodes/nodes.types';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {PatchTreeParams} from 'common/src/modules/campaignVersions/campaignVersions.api';
import getCampaignNodes from 'common/src/modules/nodes/utils/getCampaignNodes';
import {RootState} from 'common/src/redux/store';
import {updateCampaignNodes} from './updateCampaignNodes';
import {setNextNodeId} from '../campaignLogic.slice';
import {DTCreativeNode} from 'common/src/modules/decisionTree/decisionTree.types';
import campaignsApi from '../../../campaigns.api';

/**
 * Adds a card to the campaign.
 * @param props - The properties of the card to add.
 * @param dispatch - The dispatch function.
 * @param getState - The getState function.
 * @returns The response from the API.
 * @description This thunk adds a card to the campaign.
 * It gets the campaign and version number from the state,
 * calls the API to add the node,
 * and updates the nodesRef and redux state with the new nodes.
 */
export const addCard = createAsyncThunk(
  `${BreadcrumbName.CampaignLogic}/addCard`,
  async (props: {newNode: MapNode; campaignTreeType: CampaignTreeType}, {dispatch, getState}) => {
    const {newNode, campaignTreeType} = props;

    // Get campaign data from Redux state
    const state = getState() as RootState;
    const campaign = state.campaignPage?.campaignTree?.campaign;
    const versionNumber = state.campaignPage?.campaignTree?.versionNumber;

    if (!campaign || !versionNumber) {
      throw new Error('Campaign or version number not found in state');
    }

    // Call API to add the node (similar to updateTree)
    const basePayload: PatchTreeParams = {
      campaignId: campaign.id,
      versionNumber,
      nodesToAdd: [newNode],
      nodesToDelete: [],
      nodesToUpdate: [],
      campaignNode: campaign,
      ...((newNode as DTCreativeNode)?.isFallbackCreative && {
        fallbackCreativeNodeId: newNode.id,
      }),
    };

    const response = await campaignsApi.updateTree(basePayload);

    // Get updated nodes from response and update nodesRef
    const campaignNodes = getCampaignNodes(response);
    dispatch(updateCampaignNodes({connectedNodes: campaignNodes, campaignTreeType}));
    dispatch(setNextNodeId(response.nextNodeId));
  },
);
